/**
 * 이상한 방 - 매치 상태 셀렉터와 작은 갱신 헬퍼
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 11.3절(서버가 쥐는 것) · 로드맵 모델 2.2절
 *
 * ## 왜 따로 파일인가
 *
 * 리듀서 조각 다섯(`matchPhases`·`matchShoot`·`matchOrigin`·`matchScore`·`matchState`)이 전부
 * "이 사람은 술래인가"·"미발견이 몇 개인가"·"좌석 하나를 바꾼 새 상태"를 묻는다. 그 질문을 각 파일이
 * 자기 식으로 적으면 `players[id]`의 `undefined` 처리가 다섯 벌이 되고, 그중 하나가 `!`를 쓰는 날이 온다.
 * 여기 모아 두면 조각들끼리 서로를 import 하지 않아도 되어 순환도 생기지 않는다
 * (조각 → 셀렉터 한 방향. `matchView`도 같은 것을 읽는다).
 *
 * 규칙은 없다. 여기 있는 것은 전부 **읽기**와 **필드 하나를 바꾼 사본 만들기**다.
 */

import type { FloorPlan } from '../../core/floorPlan'
import { OBJECTS_PER_ZONE } from '../../core/roomGen'
import type { GenerateOptions } from '../../core/roomGen'
import type { PlayerId } from '../../core/types'
import type { MatchChange, MatchDeps, MatchPhase, MatchPlayer, MatchState, MatchStep } from './matchTypes'
import { assignZones, zoneOf, type ZoneAssignment } from './matchZones'

/* --- 결과 만들기 ------------------------------------------------------------------ */

/** 무시된 이벤트. 입력 상태를 **그대로** 돌려 참조 비교로 "변화 없음"을 알 수 있게 한다. */
export function idle(state: MatchState): MatchStep {
  return { state, effects: [] }
}

/** 두 단계를 이어 붙인다 - 앞 단계의 효과 뒤에 뒷 단계의 효과. 순서가 연출 순서다. */
export function chain(first: MatchStep, next: (state: MatchState) => MatchStep): MatchStep {
  const second = next(first.state)
  return { state: second.state, effects: [...first.effects, ...second.effects] }
}

/* --- 좌석 읽기 -------------------------------------------------------------------- */

export function playerOf(state: MatchState, id: PlayerId): MatchPlayer | undefined {
  return state.players[id]
}

/** 입장 순서대로의 좌석 목록. `order`에 있는데 `players`에 없는 id는 상태 손상이라 건너뛰지 않고 던진다. */
export function seatsInOrder(state: MatchState): readonly MatchPlayer[] {
  return state.order.map((id) => {
    const player = state.players[id]
    if (player === undefined) {
      throw new Error(`match: order에는 있는데 players에 없는 좌석 '${id}' - 상태가 손상됐다`)
    }
    return player
  })
}

/**
 * 이 매치의 평면도. 좌석 수의 함수다(`MatchDeps.planFor` 주석 - 여럿이 판은 구획 수가 좌석 수와 같다).
 *
 * 상태에 굳혀 두지 않고 매번 묻는 이유는 좌석 수가 **로비를 벗어나는 순간 이미 굳어 있기** 때문이다 -
 * 합류는 로비에서만 일어나고 진행 중 퇴장은 좌석을 남긴다. 같은 수를 상태에도 적으면 그 둘이 갈릴 자리가 생기고,
 * 갈리면 벽은 여덟 구획인데 배정은 일곱인 판이 화면에서야 발견된다.
 */
export function matchPlan(state: MatchState, deps: MatchDeps): FloorPlan {
  return deps.planFor(seatsInOrder(state).length)
}

/**
 * 이 평면도로 방을 구울 때 쓰는 생성 옵션. **목표 개수를 구획 수에서 다시 잡는다.**
 *
 * 여럿이 집은 블록이 좌석 수만큼 붙어 커지므로(`core/partyPlan`) 생성기의 고정 목표(180)를 그대로 쓰면
 * 여섯 좌석부터 그 상한에 걸린다 - **같은 27×27 블록인데 2석 판에서는 소품 50개, 8석 판에서는 23개**가 들어갔다.
 * 블록이 같으면 밀도도 같아야 한다(사용자 지시 "1블록의 크기는 동일"). 그 밀도가 곧 이상한 곳을 숨길 자리의 수다.
 *
 * **명시된 목표가 이긴다.** `deps.roomOptions.targetObjectCount`는 개발 훅과 고정 평면도 테스트가 방을 못박는 길이라
 * 여기서 덮으면 그 판들이 조용히 다른 방이 된다 - 튜닝의 "URL이 단계 값을 이긴다"와 같은 방향이다.
 */
export function roomOptionsFor(plan: FloorPlan, deps: MatchDeps): GenerateOptions {
  const options = deps.roomOptions ?? {}
  if (options.targetObjectCount !== undefined) return options
  return { ...options, targetObjectCount: plan.rooms.length * OBJECTS_PER_ZONE }
}

/**
 * 이 좌석이 아직 찾는 사람인가(관전 아님 · 끊기지 않음).
 *
 * **끊긴 좌석은 세지 않는다.** 좌석 자체는 남기지만(`matchState.onLeave` - 발견·점수·구획이 사라지면
 * 남은 사람들의 판이 거짓이 된다) 남은 사람들이 그 사람을 기다릴 이유는 없다. 바꾸기가 이미 같은 규약이다 -
 * 끊긴 좌석은 완료로 치고 마지막 미커밋 좌석이 끊기면 그 자리에서 페이즈가 끝난다.
 */
export function isHunting(player: MatchPlayer): boolean {
  return !player.spectating && player.connected
}

/**
 * 아직 쏠 자격이 있는 사람이 한 명도 없는가. `all-out`의 조건이다.
 * M11에서 역할이 사라져 좌석 전부가 찾는 사람이므로 걸러 낼 목록이 없다(옛 `hunters`는 그래서 걷었다).
 */
export function allHuntersOut(state: MatchState): boolean {
  return !seatsInOrder(state).some(isHunting)
}

export function isHost(state: MatchState, id: PlayerId): boolean {
  return state.hostId === id
}

/**
 * 지금 손으로 고칠 사람이 하나라도 있는가. 참일 때만 바꾸기 페이즈가 실제로 흐른다(`matchPhases`).
 * 전원이 이웃이거나 전원 끊긴 라운드는 아무도 없는 60초라 외우기에서 곧장 수색이고, 그 구획들은 생성기가 채운다.
 */
export function hasHumanChanger(state: MatchState): boolean {
  return seatsInOrder(state).some((player) => player.kind === 'human' && player.connected)
}

/**
 * 이 판의 구획 배정(`matchZones.assignZones`). 평면도와 같은 이유로 상태에 굳히지 않는다 -
 * 입력(시드 · 라운드 · 좌석 순서 · 평면도)이 전부 상태와 `deps`에 이미 있고, 사본을 두면 라운드가 도는
 * 순간 둘이 갈릴 자리가 생긴다.
 */
export function matchZones(state: MatchState, deps: MatchDeps): readonly ZoneAssignment[] {
  return assignZones(state.seed, state.round, state.order, matchPlan(state, deps))
}

/** 이 사람의 구획. 없으면 `undefined`(좌석보다 구획이 적은 판 - `matchZones` 머리말). */
export function zoneFor(state: MatchState, deps: MatchDeps, id: PlayerId): ZoneAssignment | undefined {
  return zoneOf(matchZones(state, deps), id)
}

/**
 * 방장 승계 후보 - 접속 중인 사람 좌석 중 입장 순서가 앞인 사람(`except`는 뺀다). CPU는 방장이 못 된다:
 * 방장 이벤트(`start`·`skipPhase`·`nextRound`)를 보내는 것은 클라이언트이고 CPU에는 클라이언트가 없다.
 */
export function nextHostCandidate(state: MatchState, except: PlayerId): PlayerId | null {
  const candidate = seatsInOrder(state).find(
    (player) => player.id !== except && player.kind === 'human' && player.connected,
  )
  return candidate?.id ?? null
}

/* --- 변화 읽기 -------------------------------------------------------------------- */

/** 아직 발견되지 않은 변화. 리빌 길이와 구획 점수의 근거다. */
export function unfoundChanges(state: MatchState): readonly MatchChange[] {
  const foundIds = new Set(state.found.map((entry) => entry.changeId))
  return state.changes.filter((change) => !foundIds.has(change.id))
}

/**
 * 아직 **찾을 사람이 남은** 이상한 곳. 미발견 중에서 주인 말고 찾는 사람이 하나라도 있는 것만이다.
 *
 * 제 구획의 것은 주인이 못 찾으므로(M11 규칙 4) 마지막 술래의 구획에만 남은 변화는 시간이 아무리 남아도
 * 이 라운드에서 영영 안 찾힌다. 그 순간이 수색의 끝이고(`matchPhases.settleHunt`), 이 함수가 그 판단의
 * 유일한 자리다 - "관전인가"와 "제 것인가"를 두 곳에서 세면 화면과 리듀서가 다른 말을 하기 시작한다.
 */
export function reachableUnfound(state: MatchState): readonly MatchChange[] {
  const hunters = seatsInOrder(state).filter(isHunting)
  return unfoundChanges(state).filter((change) => hunters.some((hunter) => hunter.id !== change.owner))
}

/** 이 사람이 찾은 것만. 찾은 점수는 이 목록으로 `hunt.computeScoreBreakdown`을 부른다. */
export function foundBy(state: MatchState, id: PlayerId) {
  return state.found.filter((entry) => entry.by === id)
}

/**
 * 이 사람이 **찾을 수 있는** 이상한 곳(내 구획 것을 뺀 나머지 - M11 규칙 4). HUD의 분모이자 퍼펙트의 기준이고,
 * 점수 계산에 넘기는 `changes`도 이것이다 - 전체를 넘기면 남의 구획을 다 찾은 사람이 영원히 퍼펙트가 아니다.
 */
export function findableChanges(state: MatchState, id: PlayerId): readonly MatchChange[] {
  return state.changes.filter((change) => change.owner !== id)
}

/** 이 사람이 찾을 수 있는 것 중 아직 안 찾힌 수. `foundChange.remaining`("N개 남았다")의 값이다. */
export function findableRemaining(state: MatchState, id: PlayerId): number {
  const foundIds = new Set(state.found.map((entry) => entry.changeId))
  return findableChanges(state, id).filter((change) => !foundIds.has(change.id)).length
}

/** 이 사람이 찾을 수 있는 것 중 이미 찾힌 수(누가 찾았든). HUD "찾음 N/M"의 분자다. */
export function findableFound(state: MatchState, id: PlayerId): number {
  return state.found.filter((entry) => entry.owner !== id).length
}

/**
 * 이 사람이 아직 찾을 수 있는 것(내 구획 제외 · 미발견). 봇의 조준(`cpu.easiestUnfound`)이 이 목록에서 고른다 -
 * 전체 미발견에서 고르면 자기 구획을 겨눠 `own-zone` 거절만 쌓는다(M11 규칙 4·7).
 */
export function findableUnfound(state: MatchState, id: PlayerId): readonly MatchChange[] {
  const foundIds = new Set(state.found.map((entry) => entry.changeId))
  return findableChanges(state, id).filter((change) => !foundIds.has(change.id))
}

/** 이 사람의 구획에서 난 것 중 아직 아무도 못 찾은 것. 그 사람의 구획 점수(M11 규칙 5)의 분자다. */
export function unfoundOf(state: MatchState, id: PlayerId): readonly MatchChange[] {
  return unfoundChanges(state).filter((change) => change.owner === id)
}

/* --- 좌석 갱신 -------------------------------------------------------------------- */

/** 좌석 하나를 바꾼 새 상태. 나머지 좌석은 같은 참조를 유지한다. */
export function withPlayer(state: MatchState, player: MatchPlayer): MatchState {
  return { ...state, players: { ...state.players, [player.id]: player } }
}

/** 좌석 하나에 패치를 얹는다. 좌석이 없으면 상태를 그대로 돌려준다 - 없는 사람을 만들어 내지 않는다. */
export function patchPlayer(
  state: MatchState,
  id: PlayerId,
  patch: Partial<MatchPlayer>,
): MatchState {
  const player = state.players[id]
  if (player === undefined) return state
  return withPlayer(state, { ...player, ...patch })
}

/** 모든 좌석에 같은 변환을 적용한다. 라운드 시작·수색 진입이 좌석 전부를 되돌릴 때 쓴다. */
export function mapPlayers(
  state: MatchState,
  transform: (player: MatchPlayer) => MatchPlayer,
): MatchState {
  const players: Record<PlayerId, MatchPlayer> = {}
  for (const player of seatsInOrder(state)) players[player.id] = transform(player)
  return { ...state, players }
}

/* --- 넘어가기 표 ------------------------------------------------------------------ */

/**
 * 앞당길 것이 있는 페이즈인가. 로비와 결과에는 없다 - 그 자리는 [시작]과 [한 판 더]가 이미 쥐고 있다.
 *
 * 리듀서(`matchPhases.onSkipVote`·`onSkipPhase`)와 투영(`matchView`)이 같은 이 함수를 본다.
 * 화면이 목록을 따로 적으면 **아무 일도 안 일어나는 버튼이 표를 세기 시작한다** -
 * 실제로 로비의 감춰진 버튼이 [바로 시작 0/2]로 보였고, 그건 누를 수 없는 셈이었다.
 */
export function canSkipPhase(phase: MatchPhase): boolean {
  return phase !== 'lobby' && phase !== 'result'
}

/**
 * 넘어가기 정족수 = **붙어 있는 사람 좌석 수**(`matchPhases.onSkipVote`).
 *
 * 끊긴 좌석을 세지 않는 것은 이 파일의 선례 그대로다 - `hasHumanChanger`(바로 위)도 `matchDesk.deskDone`도
 * 끊긴 사람을 "기다리지 않는다"로 친다. 세면 탭 하나 닫은 사람 때문에 남은 판 내내 아무도 못 넘어간다.
 * 이웃(CPU)도 세지 않는다 - 누를 손이 없다. 사람이 하나도 없는 판이면 표로는 넘어가지 않고 페이즈 시계가 끝낸다.
 *
 * 리듀서와 투영이 같은 이 함수를 부른다 - 화면이 사람 좌석을 다시 세면 그것이 정족수 규칙의 두 번째 사본이다.
 *
 * 알려진 틈: 마지막 한 명이 표를 던진 **뒤에** 다른 사람이 끊기면 정족수가 그 순간 줄지만 다시 세는 이벤트가
 * 없어 그 자리에서 넘어가지는 않는다. 페이즈 시계가 대신 끝내므로 멈추지는 않는다.
 */
export function skipVotesNeeded(state: MatchState): number {
  return seatsInOrder(state).filter((player) => player.kind === 'human' && player.connected).length
}
